/**
 * Onglet Bons de chargement/déchargement client (par contrat).
 * Liste tous les bons des transferts liés au contrat + bouton télécharger PDF.
 *
 * PM Spec 06/05/2026 — Phase 1 G2
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  ClipboardCheck, ClipboardList, Loader2, Download as DownloadIcon, Archive,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
};
const STATUS_LABEL = { signed: 'Signé', pending: 'En cours', draft: 'Brouillon' };

const ClientContractLoadingNotesTab = ({ contractId, contract }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchNotes = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      // Récupère d'abord les transferts du contrat puis pour chacun les bons
      const runsRes = await axios.get(`${API}/contracts/${contractId}/runs?limit=500`);
      const runs = runsRes.data?.items || runsRes.data || [];
      const allNotes = [];
      // Fetch en parallèle (max 20 à la fois pour ne pas surcharger)
      for (let i = 0; i < runs.length; i += 20) {
        const batch = runs.slice(i, i + 20);
        const responses = await Promise.all(
          batch.map(r => axios.get(`${API}/transfers/${r.id}/loading-notes`).catch(() => ({ data: [] })))
        );
        responses.forEach((res, idx) => {
          (res.data || []).forEach(n => {
            allNotes.push({ ...n, runRef: batch[idx].runNumber || batch[idx].shortReference });
          });
        });
      }
      // Tri date desc
      allNotes.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setItems(allNotes);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally { setLoading(false); }
  }, [contractId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const statusOf = (n) => {
    const c = (n.signatures || []).length;
    if (c >= 2) return 'signed';
    if (c >= 1) return 'pending';
    return 'draft';
  };

  const downloadPDF = async (note) => {
    try {
      const r = await axios.get(
        `${API}/transfers/${note.operationId}/loading-notes/${note.id}/pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${note.noteNumber}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Téléchargement impossible');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const downloadZip = async () => {
    if (selectedIds.size === 0) return;
    setDownloadingZip(true);
    try {
      const r = await axios.post(`${API}/loading-notes/_download-zip`,
        { noteIds: Array.from(selectedIds) },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bons_${contract?.shortReference || contractId.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${selectedIds.size} bon(s) téléchargé(s)`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur ZIP');
    } finally { setDownloadingZip(false); }
  };

  return (
    <Card data-testid="client-contract-loading-notes-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardCheck className="w-5 h-5 text-gray-400" />
          Bons signés ({items.length})
        </CardTitle>
        {selectedIds.size > 0 && (
          <Button onClick={downloadZip} disabled={downloadingZip}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="download-zip-btn">
            {downloadingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
            ZIP ({selectedIds.size})
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardCheck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucun bon signé pour ce contrat</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>N° Bon</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Transfert</TableHead>
                <TableHead>Plaque</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(n => {
                const s = statusOf(n);
                return (
                  <TableRow key={n.id} data-testid={`loading-note-row-${n.id}`}>
                    <TableCell>
                      <input type="checkbox"
                             checked={selectedIds.has(n.id)}
                             onChange={() => toggleSelect(n.id)}
                             data-testid={`select-${n.id}`} />
                    </TableCell>
                    <TableCell className="font-mono text-xs font-semibold">{n.noteNumber}</TableCell>
                    <TableCell className="text-xs">
                      {n.type === 'loading' ? (
                        <span className="inline-flex items-center gap-1"><ClipboardCheck className="w-3 h-3 text-blue-500" /> Chargement</span>
                      ) : (
                        <span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3 text-purple-500" /> Déchargement</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {n.actualDate ? new Date(n.actualDate).toLocaleDateString('fr-FR') : '—'}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-emerald-700">{n.runRef || '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{n.licensePlate || '—'}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] border ${STATUS_BADGE[s]}`}>
                        {STATUS_LABEL[s]} ({(n.signatures || []).length}/2)
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => downloadPDF(n)}
                              data-testid={`download-note-${n.id}`}>
                        <DownloadIcon className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientContractLoadingNotesTab;
